import { ee } from '../../services/earth-engine';
import { mergeProperties, retrieveExtremes, truncateDateNumberInMillis } from '../utils';
import { acquireFromDate as acquireFromDateLandSat } from '../../algorithms/satellite/landsat';
import { scoreCloudRatio } from '../imagery';
import * as Metadata from '../../common/metadata';
import { max } from 'lodash';
import { sharpenBands } from '../../common/geeSharp/sharpeners';

/**
 * @function getMedianImageSentinel
 * @description
 * get median image through Sentinel sattelite
 * 1. Get collection filtering ROI and date
 * 2. Removes clouds, cirrus and noise, then isolates only water bodies using NDWI.
 * 3. Generate thumbnail
 * 4. Rename median bands (remove "_median" suffix)
 * 5. Rounds the median values (px) and converts them back to integers
 * 6. Return thumbnail URL and processed image
 * @param {ee.Geometry} geometry - ROI
 * @param {String} dtBegin - date to begin image search
 * @param {String} dtEnd - date to end image search
 * @returns {Array} median image url and median image
 */
const getMedianImageSentinel = (geometry, dtBegin, dtEnd) => {
  var cloudBitMask = ee.Number(2).pow(10).int();
  var cirrusBitMask = ee.Number(2).pow(11).int();
  const mask = (img) => {
    var qa = img.select('QA60');
    var ma = qa.bitwiseAnd(cloudBitMask).eq(0).and(qa.bitwiseAnd(cirrusBitMask).eq(0));
    ma = ma.and(img.select(['B9']).lt(300));
    ma = ma.and(img.select(['B9']).gt(50));
    ma = ma.and(img.select(['B3']).gt(100));
    img = img.updateMask(img.select([4]).lt(1000));
    img = img.updateMask(img.select([7]).lt(300));
    ma = ma.focal_min({
      kernel: ee.Kernel.circle({ radius: 1 }),
      iterations: 1,
    });
    img = img.mask(ma);
    var ndwi_revise = img
      .select([2])
      .subtract(img.select([7]))
      .divide(img.select([2]).add(img.select([7])));
    img = img.updateMask(ndwi_revise.gt(0));
    return img;
  };

  let collection = ee.ImageCollection('COPERNICUS/S2_HARMONIZED').filterBounds(geometry).filterDate(dtBegin, dtEnd);
  collection = collection.map(mask);
  var medianImage = collection.reduce(ee.Reducer.median());
  let medianThumbURL = medianImage.getThumbURL({
    region: geometry,
    bands: ['B4_median', 'B3_median', 'B2_median'],
    dimensions: 256,
    max: 1700,
    min: 300,
    format: 'jpg',
  });
  medianImage = medianImage
    .select([
      'B1_median',
      'B2_median',
      'B3_median',
      'B4_median',
      'B5_median',
      'B6_median',
      'B7_median',
      'B8_median',
      'B8A_median',
      'B9_median',
      'B11_median',
      'B12_median',
      'QA10_median',
      'QA20_median',
      'QA60_median',
    ])
    .rename(['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B8A', 'B9', 'B11', 'B12', 'QA10', 'QA20', 'QA60']);
  medianImage = medianImage.toInt();
  return [medianThumbURL, medianImage];
};

const sliceByRevisit = (collection, startingDate, days = 0, minutes = 0) => {
  const start = ee.Date(startingDate);
  if (minutes > 0) {
    return collection.filterDate(start.advance(-1, 'minute'), start.advance(minutes, 'minute'));
  }
  start.update(null, null, null, 0, 0, 0);
  return collection.filterDate(start, start.advance(days, 'day'));
};

const getImageDate = function (image) {
  let id = image.idString;
  if (image.idString === undefined) {
    id = image.get('system:id').getInfo();
  }
  let date = id.split('/');
  date = date[2].split('_');
  date = date[0];
  const year = parseInt(date.substring(0, 4));
  const monthIndex = parseInt(date.substring(4, 6)) - 1;
  const day = parseInt(date.substring(6, 8));
  const datetime = new Date(year, monthIndex, day);
  return datetime;
};

export const addCloudShadowMaskSentinel = (collection, coordinateSystem = 'EPSG:32722') => {
  const addMask = (image) => {
    const cloudProjDist = 3; // in km // Shadow projection distance (in pixels ~ 10 m)
    const hasClouds = image.bandNames().contains('clouds');
    const band_scale = 10000; // 10000 when does not apply the SIAC or 1;
    const darkPixels = image.select('B8').lt(ee.Number(0.3).multiply(band_scale)).rename('dark_pixels');
    const shadowAzimuth = ee.Number(180).subtract(ee.Number(image.get('MEAN_SOLAR_AZIMUTH_ANGLE')));
    const cldProj = image
      .select('clouds')
      .directionalDistanceTransform(shadowAzimuth, cloudProjDist * 10)
      .reproject({ crs: coordinateSystem, scale: 10 })
      .select('distance')
      .mask()
      .rename('cloud_transform');
    const shadows = cldProj.multiply(darkPixels).rename('shadows');
    const imageMask = image.addBands([darkPixels, cldProj, shadows]);
    return ee.Image(ee.Algorithms.If(hasClouds, imageMask, image));
  };

  const combineCloudShadow = (image) => {
    const hasShadows = image.bandNames().contains('shadows');
    const is_cld_shdw_focal = image
      .select('clouds')
      .add(image.select('shadows'))
      .gt(0)
      .focalMin(10)
      .focalMax(ee.Number(100).multiply(2).divide(20))
      .reproject({ crs: coordinateSystem, scale: 10 })
      .rename('cloudmask');
    const maskedImage = image.updateMask(is_cld_shdw_focal.not());
    return ee.Image(ee.Algorithms.If(hasShadows, maskedImage, image));
  };
  const withMask = collection.map(addMask);
  return withMask.map(combineCloudShadow);
};

export const calculateCloudCoverage = (geometry, startDate = '2013-01-01', collection = null) => {
  const endDate = ee.Date(Date.now());
  try {
    let sentinelSRCollection =
      collection ||
      ee.ImageCollection('COPERNICUS/S2_HARMONIZED').filterBounds(geometry).filterDate(startDate, endDate);
    let sentinelSR_Clean = sentinelSRCollection.map((img) => {
      return img.set('join_id', img.get('system:index'));
    });
    const sentinelCloudCollection = ee
      .ImageCollection('COPERNICUS/S2_CLOUD_PROBABILITY')
      .filterBounds(geometry)
      .filterDate(startDate, endDate);
    const joinedCollection = ee.ImageCollection(
      ee.Join.saveFirst('cloudProbability').apply({
        primary: sentinelSR_Clean,
        secondary: sentinelCloudCollection,
        condition: ee.Filter.equals({
          leftField: 'join_id',
          rightField: 'system:index',
        }),
      }),
    );
    sentinelSRCollection = joinedCollection.map((image) => {
      const b9 = image.select('B9').multiply(ee.Image.constant(0.0001));
      return image.addBands(b9, null, true);
    });
    const roiCloudProbabilityCollection = sentinelSRCollection.map(function (image) {
      const cloudImage = ee.Image(image.get('cloudProbability'));
      const cloudImageClipped = cloudImage.clip(geometry);
      const clouds = cloudImageClipped.select('probability').gt(50).rename('clouds');
      const imageWithClouds = image.addBands(clouds);
      const roiArea = geometry.area();
      const cloudArea = clouds.multiply(ee.Image.pixelArea());
      const sumCloudArea = cloudArea
        .reduceRegion({
          reducer: ee.Reducer.sum(),
          geometry: geometry,
          scale: 10,
          maxPixels: 1e9,
          bestEffort: true,
        })
        .get('clouds');
      const cloudPercentage = ee.Number(sumCloudArea).divide(roiArea);
      const cloudMask = imageWithClouds.select('clouds').not().focal_max(3).focal_min(2);
      const maskedImage = imageWithClouds.updateMask(cloudMask);
      return maskedImage.set({ roiCloudPercentage: cloudPercentage });
    });
    return roiCloudProbabilityCollection;
  } catch (e) {
    console.log('Error', e);
    return ee.ImageCollection([]);
  }
};

export const maskS2ImageMask = (img, mission) => {
  const cloudBitMask = ee.Number(2).pow(10).int();
  const cirrusBitMask = ee.Number(2).pow(11).int();
  const qa = img.select(mission.bands.qa);
  let ma = qa.bitwiseAnd(cloudBitMask).eq(0).and(qa.bitwiseAnd(cirrusBitMask).eq(0));

  ma = ma.focal_min({ kernel: ee.Kernel.circle({ radius: 1 }), iterations: 1 });
  img = img.mask(ma);
  return img;
};

export const maskS2Clouds = (img, geometry, bandName, scale, startingDate, returnAsFloat = false) => {
  // Returns a number. The cloud pct for a single date
  let dtCloudMapping;
  let ret = 0.01;

  // get cloud mapping from cache
  try {
    dtCloudMapping = JSON.parse(window.sessionStorage.getItem('roiCloudCoveragePerDate')); // Safely parse JSON
  } catch (e) {
    console.error('ERROR: Failed to parse cloud coverage data from sessionStorage', e);
    return returnAsFloat ? ret : ee.Number(ret); // Return default if there's an error
  }

  const cloudPropertyKey = 'cloud';

  if (startingDate !== undefined && dtCloudMapping) {
    // TODO why we have startingDates as NANs when debugging on chrome?

    let startingDateInMillis =
      typeof startingDate === 'string' ? new Date(startingDate).getTime() : ee.Date(startingDate).millis(); // diff types can be passed to this fn (not ideal)

    const mappingWithDateDiff = dtCloudMapping.map((el) => ({
      ...el,
      diff: Math.abs(el.dt - startingDateInMillis),
    }));
    const matchingElement = mappingWithDateDiff.reduce((prev, curr) => (prev['diff'] < curr['diff'] ? prev : curr));

    if (matchingElement) {
      ret = matchingElement[cloudPropertyKey];
      return returnAsFloat ? ret : ee.Number(ret);
    }
  }

  // Default return if no match is found
  return returnAsFloat ? ret : ee.Number(ret);
};

export const acquireFromDate = (date, mission, geometry, isSharpen = false, isOneExactDate = false) => {
  if (mission.name.indexOf('LANDSAT') !== -1) {
    return acquireFromDateLandSat(date, mission, geometry);
  }
  let slice = sliceByRevisit(
    ee.ImageCollection(mission.name).filterBounds(geometry),
    date,
    isOneExactDate ? 0 : mission.cycle,
    isOneExactDate ? 1 : 0,
  );
  if (isSharpen) slice = slice.map((imgPan) => sharpenBands(imgPan, 'B8', ['B11', 'B12']));
  const mosaicked = slice.mosaic().clip(geometry);
  const bandName = 'QA60';
  const scale = 60;
  const image = mosaicked.set(mergeProperties(slice));
  const ratio = maskS2Clouds(image, geometry, bandName, scale, date);
  try {
    return image.set('CLOUDS', ratio);
  } catch (e) {
    return image.set('CLOUDS', ee.Number(0));
  }
};

export const processCollection = (mission, geometry, startDate, endDate) => {
  let query = ee.ImageCollection(mission.name).filterBounds(geometry);
  if (startDate || endDate) {
    query = query.filterDate(startDate || `${mission.startYear}-01-01`, endDate || ee.Date(Date.now()));
  }
  const process = (available) => {
    const global = retrieveExtremes(available);
    const earliestDate = global.earliest;
    const completeCycles = ee
      .Number(global.latest.difference(earliestDate, 'day').abs())
      .divide(mission.cycle)
      .ceil()
      .max(1);
    const additions = ee.List.sequence(0, null, mission.cycle, completeCycles);
    const carriers = additions.map((increment) => {
      const startingDate = earliestDate.advance(increment, 'day');
      const collection = sliceByRevisit(available, startingDate, mission.cycle);
      const empty = ee.Algorithms.IsEqual(collection.size(), 0);
      const firstImage = ee.Image(collection.sort(Metadata.TIME_START).first());
      const cloudCoverage = ee.Algorithms.If(
        empty,
        0,
        ee.Algorithms.If(
          firstImage.propertyNames().contains('CLOUDY_PIXEL_PERCENTAGE'),
          firstImage.get('CLOUDY_PIXEL_PERCENTAGE'),
          0,
        ),
      );
      const properties = ee.Algorithms.If(empty, {}, mergeProperties(collection));

      return ee.Image(0).set(properties).set('CLOUDY_PIXEL_PERCENTAGE', cloudCoverage);
    });

    return ee.ImageCollection.fromImages(carriers).filter(ee.Filter.contains(Metadata.FOOTPRINT, geometry));
  };

  return ee.ImageCollection(ee.Algorithms.If(query.size().gt(0), process(query), ee.ImageCollection([])));
};

export const generateCloudMap = (collection) => {
  const dates = collection.aggregate_array(Metadata.TIME_START).map((d) => ee.Date(d).format(null, 'UTC'));
  const clouds = collection.aggregate_array('CLOUDY_PIXEL_PERCENTAGE');
  return ee.Dictionary.fromLists(dates, clouds);
};

const queryAvailable = (mission) => (geometry, startDate, endDate) => {
  const collection = processCollection(mission, geometry, startDate, endDate);
  return generateCloudMap(collection);
};

const getAvailable = queryAvailable;

const acquire = (mission) => (date, geometry) => {
  if (window.location.href.indexOf('bathymetry') !== -1) return false;
  return acquireFromDate(date, mission, geometry, false, true);
};

const format = (properties) => {
  return properties['system:time_start'] + ' -- ' + properties['cloud'];
};

export default {
  getMedianImageSentinel,
  queryAvailable,
  getAvailable,
  acquire,
  format,
};
